import { useMemo } from 'react'
import { MultiSelectPicker } from '../ui/multi-select-picker'
import { SelectPicker, type SelectOption } from '../ui/select-picker'
import type { CaseReviewerPickerOption, CaseReviewerPickerResponse } from '../../types/case'

export const REVIEWER_TYPE_LABELS = {
  doctor: 'Doctor',
  assistant: 'Assistant',
} as const

function toOptions(items: CaseReviewerPickerOption[]): SelectOption[] {
  return items.map((item) => ({
    value: item.id,
    label: `${item.name} (${REVIEWER_TYPE_LABELS[item.reviewer_type]})`,
  }))
}

type CaseReviewerMultiPickerProps = {
  picker: CaseReviewerPickerResponse | null
  values: string[]
  onChange: (ids: string[]) => void
  disabled?: boolean
  placeholder?: string
}

/** Pick doctors or the case owner's assistants — not Profile Reviewers. */
export function CaseReviewerMultiPicker({
  picker,
  values,
  onChange,
  disabled = false,
  placeholder = 'Select reviewers',
}: CaseReviewerMultiPickerProps) {
  const options = useMemo(() => {
    if (!picker) {
      return []
    }

    return [
      ...toOptions(picker.doctors).map((option) => ({
        ...option,
        label: `${option.label}`,
      })),
      ...toOptions(picker.assistants),
    ]
  }, [picker])

  return (
    <MultiSelectPicker
      options={options}
      values={values}
      onChange={onChange}
      placeholder={placeholder}
    />
  )
}

type CaseReviewerSinglePickerProps = {
  picker: CaseReviewerPickerResponse | null
  value: string
  onChange: (id: string) => void
  disabled?: boolean
  placeholder?: string
  /** Exclude these user IDs from the list (e.g. current assignee when reassigning). */
  excludeIds?: string[]
}

export function CaseReviewerSinglePicker({
  picker,
  value,
  onChange,
  disabled = false,
  placeholder = 'Select reviewer',
  excludeIds = [],
}: CaseReviewerSinglePickerProps) {
  const options = useMemo(() => {
    if (!picker) {
      return []
    }

    const excluded = new Set(excludeIds)
    const all = [...(picker.doctors ?? []), ...(picker.assistants ?? [])].filter(
      (item) => !excluded.has(item.id),
    )

    return toOptions(all)
  }, [excludeIds, picker])

  return (
    <SelectPicker
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
    />
  )
}

export function assignedReviewerOptions(reviewers: Array<{ user_id: string; user?: { name?: string } | null; reviewer_type?: string | null }>): SelectOption[] {
  return reviewers.map((reviewer) => ({
    value: reviewer.user_id,
    label: reviewer.user?.name
      ? `${reviewer.user.name}${reviewer.reviewer_type ? ` (${reviewer.reviewer_type})` : ''}`
      : reviewer.user_id,
  }))
}
